import { useCallback, useEffect, useRef, useState } from 'react';
import type { CursorPage } from '@dru/types';
import { errorMessage } from './auth';

/**
 * A cursor-paged list with "Load more".
 *
 * `useQuery` fetches one page and stops, which is why several lists showed
 * the first fifty rows of a company and nothing after them. This keeps the
 * pages it has fetched, appends the next one on demand, and starts over when
 * the filters change. `DataTable` already knows how to render the button;
 * this is the half that was missing.
 */
export function usePages<T>(
  fetchPage: (cursor: string | null) => Promise<CursorPage<T> | { items: T[]; nextCursor: string | null }>,
  deps: unknown[] = [],
) {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // Every row the query matches, where the endpoint says (`total`).
  const [total, setTotal] = useState<number | undefined>(undefined);
  // Bumped when the filters change, so a "Load more" still in flight for the
  // previous venue or filter is dropped rather than appended to the new list.
  const generation = useRef(0);
  const fetcher = useRef(fetchPage);
  fetcher.current = fetchPage;

  useEffect(() => {
    let live = true;
    generation.current += 1;
    setLoading(true); setError(null);
    fetcher.current(null)
      .then((p) => { if (live) { setItems(p.items); setCursor(p.nextCursor ?? null); setTotal((p as CursorPage<T>).total); } })
      .catch((e) => { if (live) setError(errorMessage(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true); setError(null);
    const gen = generation.current;
    try {
      const p = await fetcher.current(cursor);
      if (gen !== generation.current) return;
      setItems((prev) => [...prev, ...p.items]);
      setCursor(p.nextCursor ?? null);
      setTotal((p as CursorPage<T>).total);
    } catch (e) { if (gen === generation.current) setError(errorMessage(e)); } finally { setLoadingMore(false); }
  }, [cursor, loadingMore]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { items, loading, loadingMore, error, hasMore: cursor !== null, loadMore, reload, total };
}
